import React, { useCallback, useMemo,useState,useLayoutEffect } from 'react';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';

import DatePicker from 'antd/es/date-picker';
import Divider from 'antd/es/divider';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';

import featureToggles from 'config/featureToggles';
import { datePickerFormats } from 'config/common';
import { timeFormat } from "config/common";

import { formatDate } from "helpers/date";

import BookmarksService from 'services/bookmarks';
import useResource from 'hooks/useResource';
import { ResourceType } from 'services/resources';

import { useDateRangePicker } from 'hooks/useDatePicker';
import useQueryParam from 'hooks/useQueryParam';
import useDoorDetails from 'hooks/useDoorDetails';
import useModal from 'hooks/useModal';

import MainLayout from 'layouts/Main.layout';
import PageHeader from 'components/MainPageHeader';
import BackButton from 'components/BackButton';
import RadioGroup from 'components/RadioGroup';
import ViewSwitcher from 'components/ViewSwitcher';
import { Row, Col, Space } from 'components/layout';
import { LoadingText, Text } from 'components/Text';
import FilterBar from 'components/FilterBar';
import Button from 'components/Button';
import Icon from 'components/Icon';
import { CameraViewModal } from 'components/process/CameraView';
import  BtcViewModal  from 'components/process/BtcViewModal';
import { useFeatureToggleItem } from 'components/FeatureToggle';

import DoorDetailsTabs from '../components/DoorDetailsTabs';
import DoorDetailsInfoCard from '../components/DoorDetailsInfoCard';
import DoorActivityTable from '../components/DoorActivityTable';
import DoorActivityStats from '../components/DoorActivityStats';
import DoorEmployeesTable from '../components/DoorEmployeesTable';
import EmployeesListModal from '../components/EmployeesListModal';

import { DOOR_DETAILS_TABS, DOOR_FILTER_LEVELS } from '../constants';
import { PROCESS_DIRECTION_LABELS } from "helpers/process";

const DoorDetailsPageHeader = styled(PageHeader)`
  & .ant-page-header-back {
    margin-left: 2px;
    margin-right: 12px;
  }

  & .ant-page-header-heading-title {
    margin-right: 16px;
  }

  & .ant-page-header-heading-sub-title {
    display: inline-flex;
    flex: 1;
    margin-right: 100px;

    & > .door-details-tabs {
      flex: 1;
      margin-left: 16px;
      margin-right: 180px;

      & .ant-tabs-nav-wrap {
        justify-content: center;
      }
    }
  }

  & .ant-page-header-content {
    padding-top: unset;
    width: 148px;
    margin-left: 32px;
    margin-top: -12px;
  }
`;

const DoorDetailsPage = () => {
  const history = useHistory();
  const params = useParams();
  const individualsFeatureToggle = useFeatureToggleItem(featureToggles.FEATURE_INDIVIDUALS);
  const showIndividuals = individualsFeatureToggle?.visible ?? true;
  const [currentRefreshed, setCurrentRefreshed] = useState();
   
  const [selectedTab, setSelectedTab] = useQueryParam('tab', DOOR_DETAILS_TABS.ACTIVITY.value);
  const [selectedLevel, setSelectedLevel] = useQueryParam('activity', DOOR_FILTER_LEVELS.DETAILS.value);
  const dateRangePicker = useDateRangePicker('daily', {
    initialValue: [moment().subtract(1, 'week').startOf('day'), moment().endOf('day')],
    queryParam: 'dt',
  });
  const employeesModal = useModal();
  const cameraViewModal = useModal();
  const btcViewModal = useModal();
  const [btcButtonKey, setBtcButtonKey] = useState(0);
  const {
    isLoading,
    doorDetails,
    doorDetailsData,
    timelinesData,
  } = useDoorDetails(params.id, {
    start: dateRangePicker.dateRange[0],
    end: dateRangePicker.dateRange[1],
  });
  const { state: locationsState } = useResource(ResourceType.locations, {
    prefetch: true,
  });

  const locationTimezone = useMemo(() => {
    return locationsState.list[0] && !locationsState.isLoading ? locationsState.list[0].timezone_offset : null;
  }, [locationsState])

  const toggleFavorite = useCallback((e) => {
    e.stopPropagation();

    const bookmarks = new BookmarksService();
    const { door } = doorDetails.store.get().stats;
    const { door_id: doorId } = door;

    if (door.is_favorite === 1) {
      bookmarks.remove({ door_id: doorId });
      door.is_favorite = 0;
    } else {
      bookmarks.add({ door_id: doorId });
      door.is_favorite = 1;
    }

    doorDetails.store.set(state => ({
      stats: {
        ...state.stats,
        door,
      }
    }));

  }, [doorDetails.store]);

  const onEmployeesLinkClicked = useCallback((item) => {
    employeesModal.show(item);
  }, [employeesModal]);

  const onOpenCameraView = useCallback((startTs, endTs) => {
    if (doorDetailsData.door) {
      const { door_id, timezone_offset } = doorDetailsData.door;
      cameraViewModal.show({
        startTs,
        endTs,
        doorId: door_id,
        tzOffset: timezone_offset
      });
    }
  }, [cameraViewModal, doorDetailsData]);

  const onCloseCameraView = useCallback(() => {
    cameraViewModal.hide();
  }, [cameraViewModal]);

  const onOpenBtcView = useCallback(
    (doorId) => {
      btcViewModal.show({
        doorId,
      });
    },
    [btcViewModal]
  );

  const onCloseBtcView = useCallback(() => {
    btcViewModal.hide();
    setBtcButtonKey(prevKey => prevKey + 1);
  }, [btcViewModal]);

  const canGoBack = useMemo(() => {
    return history.length > 2;
  }, [history]);
  
    useLayoutEffect(() => {
      setCurrentRefreshed(moment().utcOffset(locationTimezone))
      const updateCurrentDate = () => {
        setCurrentRefreshed(moment().utcOffset(locationTimezone));
      };
      const intervalId = setInterval(updateCurrentDate, 180000);
      return () => clearInterval(intervalId);
    }, [locationTimezone]);

    const processDirection = useMemo(() => {
      if (!doorDetailsData.door) return null;
  
      return PROCESS_DIRECTION_LABELS[doorDetailsData.door.direction] || "";
    }, [doorDetailsData]);

  return (
    <MainLayout>
      <DoorDetailsPageHeader
        title={
          !doorDetailsData.door ? (
            <LoadingText style={{ marginRight: '16px' }}>Loading</LoadingText>
          ) : (
              <Text style={{ display: 'inline-block', lineHeight: 1.2, marginLeft: canGoBack ? 0 : '8px' }}>
              Door {doorDetailsData.door.name}
              <Text
                style={{
                  display: "flex",
                  flexDirection: "column",
                  fontSize: "12px",
                  lineHeight: 0.6,
                  color: "#A4B0BA",
                  fontWeight: 400,
                  marginLeft: "2px",
                }}
              >
                {processDirection}
              </Text>
            </Text>
          )
        }
        subTitle={(
          <>
            <Space size={12}>
              {doorDetailsData.door && doorDetailsData.door.hasUnidentifiedEmployees && (
                <Tooltip title="Unidentified individual">
                  <Icon
                    name="user"
                    size={16}
                    fill="#CF1322"
                    color="white"
                    strokeWidth="3"
                    style={{
                      backgroundColor: '#CF1322',
                      borderRadius: '32px',
                      width: '24px',
                      height: '24px',
                      display: 'inline-flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginRight: '4px',
                    }}
                  />
                </Tooltip>
              )}
              {doorDetailsData.door && (doorDetailsData.door.warningMessage || doorDetailsData.door.is_low_accuracy===1) && (
                <Tooltip title={doorDetailsData.door.is_low_accuracy===1? 'Low accuracy':doorDetailsData.door.warningMessage}>
                  <Icon
                    name="alert-triangle"
                    size={20}
                    fill="yellow"
                    color="black"
                    strokeWidth="3"
                    style={{
                      borderRadius: '32px',
                      width: '24px',
                      height: '24px',
                      display: 'inline-flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginRight: '4px',
                    }}
                  />
                </Tooltip>
              )}
              {doorDetailsData.door && (
                <Icon
                  name="star"
                  size={24}
                  color={doorDetailsData.door.is_favorite === 1 ? '#FAAD14' : '#B9C2C9'}
                  fill={doorDetailsData.door.is_favorite === 1 ? '#FAAD14' : ''}
                  onClick={toggleFavorite}
                  style={{
                    marginTop: '2px',
                    marginRight: '4px',
                  }}
                />
              )}
              <Button
                type="primary"
                size="small"
                icon={{
                  name: 'video',
                  size: 16,
                }}
                onClick={() => {
                  const now = Math.floor(new Date() / 1000);
                  let startTs = now - (3600 * 24), endTs = now;

                  if (timelinesData.activeTimeline) {
                    const { unloading } = timelinesData.activeTimeline;
                    startTs = unloading.docked_at;
                    endTs = unloading.undocked_at || now;
                  }

                  // NOTE: replace with values from query params if provided
                  const queryParams = new URLSearchParams(window.location.search);
                  if (queryParams.has('cameraViewStartDt')) {
                    startTs = Date.parse(queryParams.get('cameraViewStartDt')) / 1000;
                  }
                  if (queryParams.has('cameraViewEndDt')) {
                    endTs = Date.parse(queryParams.get('cameraViewEndDt')) / 1000;
                  }

                  onOpenCameraView(startTs, endTs);
                }}
              >
                Camera View
              </Button>

              <Button
                key={btcButtonKey}
                type="primary"
                size="small"
                icon={{
                  name: "monitor",
                  size: 16,
                }}
                onClick={() => {
                  const doorId = params.id;

                  onOpenBtcView(doorId);
                }}
              >
                BTC View
              </Button>
            </Space>
            {showIndividuals && (
              <DoorDetailsTabs
                className="door-details-tabs"
                centered={true}
                defaultActiveKey={selectedTab}
                onChange={setSelectedTab}
              />
            )}
          </>
        )}
        backIcon={
          canGoBack ? <BackButton /> : false
        }
        onBack={() => {
          history.goBack();
        }}
      /> 
       {doorDetailsData.door && locationTimezone && currentRefreshed && <Typography.Text
          style={{
            fontSize: "12px",
            lineHeight: "20px",
            color: "#A4B0BA",
            paddingLeft:canGoBack? "80px":"34px"
          }}
        >
          {`Refreshed at: ${formatDate(currentRefreshed , timeFormat,{tzOffset:locationTimezone})}`}
        </Typography.Text>}

      {selectedTab === DOOR_DETAILS_TABS.ACTIVITY.value && (
        <Row style={{ padding: '12px 26px' }}>
          <DoorDetailsInfoCard
            door={doorDetailsData.door}
            currentContainer={timelinesData.activeTimeline}
            containers={timelinesData.timelines}
            openCameraView={onOpenCameraView}
          />
        </Row>
      )}

      {selectedTab === DOOR_DETAILS_TABS.ACTIVITY.value && (
        <Row justify="center" style={{ padding: '20px 24px' }}>
          <RadioGroup
            defaultValue={selectedLevel}
            options={DOOR_FILTER_LEVELS.entries()}
            optionType="button"
            buttonStyle="solid"
            onChange={e => setSelectedLevel(e.target.value)}
          />
        </Row>
      )}

      <Row style={{ padding: `${selectedTab === DOOR_DETAILS_TABS.ACTIVITY.value ? '12px' : '20px'} 24px` }}>
        <FilterBar
          title={DOOR_DETAILS_TABS.valueOf(selectedTab).title}
          titleStyles={{ fontSize: '20px', lineHeight: '28px', marginLeft: '8px' }}
          size="large"
        >
          <FilterBar.Slot align="left">
            <DatePicker.RangePicker
              picker="date"
              format={datePickerFormats.daily}
              defaultValue={dateRangePicker.dateRange}
              onChange={(dates) => {
                if (dates) {
                  dateRangePicker.setDateRange(dates);
                }
              }}
              allowClear={false}
            />
          </FilterBar.Slot>
        </FilterBar>
      </Row>

      <Row style={{ padding: '0 24px' }}>
        <Col span={24}>
          <ViewSwitcher currentKey={selectedTab}>
            <ViewSwitcher.View viewKey={DOOR_DETAILS_TABS.ACTIVITY.value}>
              <ViewSwitcher currentKey={selectedLevel}>
                <ViewSwitcher.View viewKey={DOOR_FILTER_LEVELS.DETAILS.value}>
                  <DoorActivityTable
                    isLoading={isLoading}
                    dataSource={doorDetailsData.stats}
                    showIndividuals={showIndividuals}
                    onEmployeesLinkClicked={onEmployeesLinkClicked}
                  />
                </ViewSwitcher.View>
                <ViewSwitcher.View viewKey={DOOR_FILTER_LEVELS.STATS.value}>
                  <Divider style={{ margin: 0, padding: '4px 0' }} />
                  <Row style={{ paddingTop: '12px', paddingBottom: '30px' }}>
                    <Col span={24}>
                      <DoorActivityStats
                        data={doorDetailsData.summary}
                      />
                    </Col>
                  </Row>
                </ViewSwitcher.View>
              </ViewSwitcher>

            </ViewSwitcher.View>
            <ViewSwitcher.View viewKey={DOOR_DETAILS_TABS.INDIVIDUALS.value}>
              <DoorEmployeesTable
                isLoading={isLoading}
                dataSource={doorDetailsData.employees}
              />
            </ViewSwitcher.View>
          </ViewSwitcher>
        </Col>
      </Row>

      <EmployeesListModal
        modal={employeesModal}
      />

      <CameraViewModal
        {...cameraViewModal.data}
        show={cameraViewModal.isVisible}
        onClose={onCloseCameraView}
      />

      <BtcViewModal
        {...btcViewModal.data}
        show={btcViewModal.isVisible}
        onClose={onCloseBtcView}
      />

    </MainLayout>
  );
};

export default DoorDetailsPage;